import React, { Fragment, forwardRef, useEffect, useRef } from 'react';
import { mergeRefs } from 'use-callback-ref';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { LoadingButton } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import Tooltip from '@atlaskit/tooltip';
import { Box } from '@atlaskit/primitives';
import { AI_BRANDNAME } from '@atlassian/jira-atlassian-intelligence/src/common/constants.tsx';
import { AiIcon } from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-icon/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	useIsAiEnabledForIssue,
	useIssueKey,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	useActions,
	useIsGenerating,
	useHasClickSummaryPanelCloseButton,
} from '@atlassian/jira-issue-smart-request-summary-state/src/controllers/state/index.tsx';
import { useIsPremium } from '@atlassian/jira-platform-react-hooks-use-ai-opt-in/src/index.tsx';
import {
	fireUIAnalytics,
	MountEvent,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectKey, useEdition } from '@atlassian/jira-project-context-service/src/main.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';

export const SMART_SUMMARY_BUTTON_ID = 'smartSummaryButton';

export const Trigger = forwardRef<
	HTMLButtonElement,
	{ totalComments?: number; isCompact: boolean }
>(({ totalComments, isCompact }, ref) => {
	const { formatMessage } = useIntl();
	const isAiOptInEnabled = useIsAiEnabledForIssue();
	const isPremium = useIsPremium();
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const edition = useEdition(projectKey, true);
	const isFeatureEnabled = isPremium && isAiOptInEnabled;
	const [isGenerating] = useIsGenerating();
	const { loadSummaryStream } = useActions();
	const [hasClickSummaryPanelCloseButton] = useHasClickSummaryPanelCloseButton();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const mergedRef = mergeRefs([ref, triggerRef]);
	useEffect(() => {
		if (!fg('jfp_a11y_issue_comments_summary_panel_focus')) {
			return;
		}
		if (hasClickSummaryPanelCloseButton) {
			triggerRef?.current?.focus();
		}
	}, [hasClickSummaryPanelCloseButton]);

	if (!isFeatureEnabled) {
		return null;
	}

	const onSummarizeButtonClick = (analyticsEvent: UIAnalyticsEvent) => {
		loadSummaryStream();
		fireUIAnalytics(analyticsEvent, 'jiraSmartSummarizeComments', {
			numberOfComments: totalComments,
		});
		fireTrackAnalytics(analyticsEvent, 'aiInteraction initiated', {
			aiFeatureName: 'jiraSmartSummarizeComments',
			proactiveAIGenerated: 0,
			userGeneratedAI: 1,
			edition,
			invokedFrom: SMART_SUMMARY_BUTTON_ID,
		});
	};

	const renderVisualRefreshIconButton = () => {
		if (totalComments === 0) {
			// If button is disabled, we don't want to render it as per BLU-5378
			return null;
		}

		return (
			<IconButton
				ref={fg('jfp_a11y_issue_comments_summary_panel_focus') ? mergedRef : ref}
				appearance="subtle"
				spacing="compact"
				icon={() => <AtlassianIntelligenceIcon color="currentColor" label={AI_BRANDNAME} />}
				isDisabled={totalComments === 0}
				isLoading={isGenerating}
				testId="issue-smart-request-summary-trigger.ui.jira-smart-summary-standard-button"
				onClick={(event, analyticsEvent) => {
					onSummarizeButtonClick(analyticsEvent);
				}}
				label={formatMessage(messages.summarize)}
				interactionName="ai-smart-summaries.ui.issue-button"
			/>
		);
	};

	const A11YWrapped = fg('jfp_a11y_team_issue_modal_non_modal_dialog_update') ? Box : Fragment;

	const StandardSummaryButton = (
		<Tooltip content={formatMessage(isCompact ? messages.compactTooltip : messages.tooltip)}>
			{/* Will only trigger the mounted events if totalComments > 0 */}
			{(totalComments || 0) > 0 && (
				<MountEvent
					onMount={(event) => fireTrackAnalytics(event, 'jiraSmartSummarizeComments mounted')}
				/>
			)}
			<A11YWrapped
				{...(fg('jfp_a11y_team_issue_modal_non_modal_dialog_update') && {
					role: 'dialog',
					'aria-modal': 'false',
					'aria-label': formatMessage(messages.summarize),
				})}
			>
				{isVisualRefreshEnabled() ? (
					renderVisualRefreshIconButton()
				) : (
					<LoadingButton
						ref={fg('jfp_a11y_issue_comments_summary_panel_focus') ? mergedRef : ref}
						spacing="compact"
						iconBefore={
							<AiIcon isDisabled={totalComments === 0} label={AI_BRANDNAME} size="small" />
						}
						isDisabled={totalComments === 0}
						isLoading={isGenerating}
						testId="issue-smart-request-summary-trigger.ui.jira-smart-summary-standard-button"
						onClick={(event, analyticsEvent) => {
							onSummarizeButtonClick(analyticsEvent);
						}}
						interactionName="ai-smart-summaries.ui.issue-button"
					>
						{!isCompact && formatMessage(messages.summarize)}
					</LoadingButton>
				)}
			</A11YWrapped>
		</Tooltip>
	);

	return StandardSummaryButton;
});
